import type {
  DELIVERY_PROPOSAL_CONTRACT_STATUS,
  OFFER_CARGO_PROCESS_PROPOSAL_STATUS,
  OFFER_CARGO_PROCESS_RECEPTION_STATUS,
} from "@/features/offer-cargo-process-status/badge-status";
import { PACKING_STYLES_LIST } from "@/shared/constants/offer-cargo";
import { formatMoney } from "@/shared/lib/currency";
import type { Loadable, LoadableData } from "@/shared/lib/loadable";
import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import type { FreightMatching } from "@x-mile/api-contract";
import type React from "react";
import { useMemo } from "react";
import type { CarrierCargoListPageProps } from "../../model";
import { mapDraftOfferCargoToCarrierCargoTableData } from "./utils";

export type CarrierCargoTableData = {
  id: number;
  remarks: OFFER_CARGO_PROCESS_PROPOSAL_STATUS[];
  departure: string;
  loadingOn: string;
  destination: string;
  unloadingOn: string;
  transitCount: number;
  shippingFee: number;
  name: string;
  packingStyle: FreightMatching.schemas.OfferCargoPackingStyle;
  receptionStatus: Loadable<OFFER_CARGO_PROCESS_RECEPTION_STATUS>;
  contractStatus: Loadable<DELIVERY_PROPOSAL_CONTRACT_STATUS | undefined>;
  expiration: string;
};

export type CarrierCargoTableProps = {
  user: LoadableData<CarrierCargoListPageProps["user"]>;
  draftOfferCargos: LoadableData<CarrierCargoListPageProps["draftOfferCargos"]>;
};

export const CargoTable: React.FC<CarrierCargoTableProps> = (props) => {
  const data = useMemo(
    () =>
      props.draftOfferCargos.data.map<CarrierCargoTableData>((offerCargo) =>
        mapDraftOfferCargoToCarrierCargoTableData(offerCargo, props.user),
      ),
    [props.draftOfferCargos.data, props.user],
  );

  const columns: Column<CarrierCargoTableData>[] = [
    {
      key: "remarks",
      label: "",
      cell: (row) => {
        return (
          <Stack spacing={1} minWidth={72}>
            {row.remarks.map((remark) => (
              <OfferCargoStatusBadge
                key={remark}
                statusType="offer_cargo_process_proposal"
                status={remark}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      key: "loadingOn",
      label: "積日時/積地",
      sortable: true,
      filterable: true,
      cell: (row) => {
        return (
          <div>
            <Typography fontWeight={700} color={grey[900]}>
              {row.departure}
            </Typography>
            {row.loadingOn ? <Typography>{row.loadingOn}</Typography> : null}
          </div>
        );
      },
    },
    {
      key: "unloadingOn",
      label: "卸日時/卸地",
      sortable: true,
      filterable: true,
      cell: (row) => {
        return (
          <div>
            <Typography fontWeight={700} color={grey[900]}>
              {row.destination}
            </Typography>
            <Typography>{row.unloadingOn}</Typography>
          </div>
        );
      },
    },
    {
      key: "transitCount",
      label: "経由地",
      align: "center",
      cell: (row) => {
        const transitCount = row.transitCount;
        if (typeof transitCount !== "number") {
          console.error(`Invalid transitCount: ${transitCount}`);
          return <>ー</>;
        }
        if (transitCount > 0) {
          return (
            <Typography fontWeight={700} fontSize={18}>
              {transitCount}
            </Typography>
          );
        }
        return <>ー</>;
      },
    },
    {
      key: "shippingFee",
      label: "運賃",
      sortable: true,
      cell: (row) => {
        return (
          <Typography>
            {row.shippingFee === 0
              ? "提案ください"
              : formatMoney(row.shippingFee)}
          </Typography>
        );
      },
    },
    {
      key: "name",
      label: "品名/荷姿",
      filterable: true,
      cell: (row) => {
        const packingStyleText = PACKING_STYLES_LIST.find(
          (style) => style.value === row.packingStyle,
        );
        return (
          <Typography>
            {row.name}/{packingStyleText?.label}
          </Typography>
        );
      },
    },
    {
      key: "receptionStatus",
      label: "受付状況",
      sortable: true,
      cell: (row) => {
        const receptionStatus = row.receptionStatus;
        if (!receptionStatus.data) {
          return <CircularProgress size={16} />;
        }
        return (
          <OfferCargoStatusBadge
            statusType="offer_cargo_process_reception"
            status={receptionStatus.data}
          />
        );
      },
    },
    {
      key: "contractStatus",
      label: "契約状況",
      sortable: true,
      cell: (row) => {
        const contractStatus = row.contractStatus;
        if (contractStatus.status !== "success") {
          return <CircularProgress size={16} />;
        }
        if (!contractStatus.data) {
          return <Typography color={grey[900]}>ー</Typography>;
        }
        return (
          <OfferCargoStatusBadge
            statusType="delivery_proposal_contract"
            status={contractStatus.data}
          />
        );
      },
    },
    {
      key: "expiration",
      label: "受付期限",
      sortable: true,
      cell: (row) => {
        const expiration = row.expiration;
        return <Typography color={grey[900]}>{expiration}</Typography>;
      },
    },
  ];

  return (
    <BaseTable
      rows={data}
      columns={columns}
      to="/carrier/cargos/$id"
      params={(val) => ({ id: val.id })}
      noDataMessage="条件に合う荷物が見つかりませんでした。"
    />
  );
};
